// @flow
import { action, computed, observable, isObservableArray } from "mobx";
import { arrayMove } from "sulu-admin-bundle/utils";
import { ResourceRequester } from "sulu-admin-bundle/services";
import type { IObservableValue } from "mobx/lib/mobx";

// submiting all atributes, not just ids on save
// but it have to work with just ids on initial load
// additional logic for props changes is needed

//CustomMultiSelectionStore
export default class CustomStore<T = string | number, U: { id: T } = Object> {
    @observable items: Array<U> = [];
    @observable loading: boolean = false;
    resourceKey: string;
    locale: ?IObservableValue<string>;
    idFilterParameter: string;
    requestParameters: { [string]: mixed };

    constructor(
        resourceKey: string,
        selectedItemIds: Array<T>,
        locale: ?IObservableValue<string>,
        idFilterParameter: string = "ids",
        requestParameters: { [string]: mixed } = {},
        matchApiData
    ) {
        this.resourceKey = resourceKey;
        this.locale = locale;
        this.idFilterParameter = idFilterParameter;
        this.requestParameters = requestParameters;
        this.matchApiData = matchApiData;
        this.loadItems(selectedItemIds);
    }

    @computed get ids(): Array<T> {
        // TODO use metadata instead of hardcoded id
        return this.items.map((item) => item.id);
    }

    @action set(items: Array<U>) {
        this.items = items;
    }

    getById(id: T): ?U {
        // TODO use metadata instead of hardcoded id
        return this.items.find((item) => item.id === id);
    }

    @action removeById(id: T) {
        // TODO use metadata instead of hardcoded id
        this.items.splice(
            this.items.findIndex((item) => item.id === id),
            1
        );
    }

    @action move(oldItemIndex: number, newItemIndex: number) {
        this.items = arrayMove(this.items, oldItemIndex, newItemIndex);
    }

    @action setLoading(loading: boolean) {
        this.loading = loading;
    }

    setRequestParameters(requestParameters: { [string]: mixed }) {
        this.requestParameters = requestParameters;
    }

    @action reorderItems(playerIds, players) {
        const playersMap = {};
        players.forEach((player) => {
            playersMap[player.id] = player;
        });

        const reorderedPlayers = playerIds
            .map((id) => playersMap[id])
            .filter(Boolean);

        this.items = reorderedPlayers;
    }

    loadItems(itemIds: ?Array<T>) {
        if (!itemIds || itemIds.length === 0) {
            this.set([]);
            return;
        }
        this.setLoading(true);
        return ResourceRequester.getList(this.resourceKey, {
            ...this.requestParameters,
            locale: this.locale ? this.locale : undefined,
            [this.idFilterParameter]: itemIds.join(","),
            limit: undefined,
            page: 1,
        }).then(
            action((data) => {
                let items = data._embedded[this.resourceKey];

                const uniqueItems = new Set(items.map((item) => item.id));
                if (items.length !== uniqueItems.length) {
                    items = [...uniqueItems].map((id) =>
                        items.find((item) => item.id === id)
                    );
                }
                const players =
                    this.matchApiData.allMatchPlayers ||
                    this.matchApiData.allTeamPlayers;

                items = items.map((item) => {
                    let updatedItem = { ...item };

                    const isUmpire =
                        this.matchApiData.umpiresObject &&
                        Object.keys(this.matchApiData.umpiresObject).length &&
                        this.matchApiData.umpiresObject[item.id];

                    const isStaff =
                        this.matchApiData.allMatchStaff &&
                        Object.keys(this.matchApiData.allMatchStaff).length &&
                        this.matchApiData.allMatchStaff[item.id];

                    const singlePlayer = isObservableArray(players)
                        ? players.find((p) => p.contact === item.id)
                        : players?.[item.id];

                    let total = "";

                    if (isUmpire) {
                        const {
                            costPersonalTransport,
                            costPublicTransport,
                            costFood,
                            costFixed,
                            costOvernight,
                        } = this.matchApiData.umpiresObject[item.id];
                        const costs = [
                            costPersonalTransport,
                            costPublicTransport,
                            costFood,
                            costFixed,
                            costOvernight,
                        ].map((el) => Number(el));
                        total = costs.reduce(
                            (acc, currentValue) => acc + currentValue,
                            0
                        );

                        updatedItem = {
                            ...updatedItem,
                            umpireRole:
                                this.matchApiData.umpiresObject[item.id]?.role,
                            costOvernight:
                                this.matchApiData.umpiresObject[item.id]
                                    ?.costOvernight || "",
                            costFixed:
                                this.matchApiData.umpiresObject[item.id]
                                    ?.costFixed || "",
                            costFood:
                                this.matchApiData.umpiresObject[item.id]
                                    ?.costFood || "",
                            costPublicTransport:
                                this.matchApiData.umpiresObject[item.id]
                                    ?.costPublicTransport || "",
                            costPersonalTransport:
                                this.matchApiData.umpiresObject[item.id]
                                    ?.costPersonalTransport || "",
                            total: total ? parseFloat(total).toFixed(2) : "",
                        };
                    }
                    if (isStaff) {
                        updatedItem = {
                            ...updatedItem,
                            role: this.matchApiData.allMatchStaff[item.id]
                                ?.matchRole,
                        };
                    }
                    if (singlePlayer) {
                        updatedItem = {
                            ...updatedItem,
                            isGoalkeeper: singlePlayer.isGoalkeeper,
                            isCaptain: singlePlayer.isCaptain,
                            isSubstituteGoalkeeper:
                                singlePlayer.isSubstituteGoalkeeper,
                            shirtNumber: singlePlayer.number,
                            haseRedCard: singlePlayer.hasRedCard,
                            isInCoreSquad: singlePlayer.core,
                            isStartingPlayer: singlePlayer.isStartingPlayer,
                            positions: singlePlayer.positions,
                            teamPositions: singlePlayer.teamPositions,
                            position: singlePlayer.position,
                        };
                    }
                    return updatedItem;
                });
                // TODO use metadata instead of hardcoded id

                items.sort(
                    (item1, item2) =>
                        itemIds.indexOf(item1.id) - itemIds.indexOf(item2.id)
                );

                this.set(items);
                this.setLoading(false);
            })
        );
    }
}
